import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Global from '../../store/global-selectors';
import { EditColumnsModal } from '../../components/modals/edit-columns-modal';
import baseColumns from '../../pages/products-page/columns';
import {
  sortedColumnConfig,
  ActiveShippingLocationsColumnId,
  defaultColumnConfig,
} from '../../store/products/grid/default-column-configuration';
import { saveUserSettingGridConfig } from '../../store/user/user.actions';
import { ProductsGridViewIds } from '../../constants/grid-view-ids';
import { UserSettings } from '../../enums/user-settings/user-settings';
import { useUserSetting } from '../../hooks/use-user-setting';
import { useShouldDisplayAutoRoutingControls } from '../../hooks/auto-routing/use-should-display-auto-routing-controls';
import { useIsAutoSplitEnabledForSeller } from '../../hooks/use-is-auto-splitting-enabled-for-seller';
import { useEnableCommittedInventoryCounts } from '../../feature-flags/hooks/use-enable-committed-inventory-counts';

const tempDefaultColumnConfigurations = sortedColumnConfig.map((col) => {
  const mappedBase = baseColumns[col.id] || baseColumns[col.mappedId];
  return {
    ...col,
    getMessageDescriptor: mappedBase?.getMessageDescriptor,
  };
});

export const EditProductsColumnsModalContainer = ({ onClose }) => {
  const dispatch = useDispatch();
  const saveColumnConfiguration = useCallback(
    (columns) => {
      dispatch(saveUserSettingGridConfig(ProductsGridViewIds.PRODUCTS, ProductsGridViewIds.PRODUCTS, ProductsGridViewIds.DEFAULT, columns));
    },
    [dispatch],
  );

  const shouldUseAggregateInventory = useSelector(Global.selectProductshouldUseAggregateInventory);
  const userSetColumnConfiguration = useUserSetting(UserSettings.productGridDefaultConfig.settingName);
  const tempUserColumnConfig = userSetColumnConfiguration ? JSON.parse(userSetColumnConfiguration) : defaultColumnConfig;
  const isSmartRoutingEnabled = useShouldDisplayAutoRoutingControls();
  const useAutoSplitEnabled = useIsAutoSplitEnabledForSeller();
  const isCommittedInventoryCountsEnabled = useEnableCommittedInventoryCounts();

  const applyColumnConfigurationFilters = useCallback(
    (column) => {
      const filteredColumnMap = {
        AutoSplit: useAutoSplitEnabled,
        AggregrateInventoryCounts: shouldUseAggregateInventory,
        Committed: isCommittedInventoryCountsEnabled,
      };

      if (column.id === ActiveShippingLocationsColumnId && !isSmartRoutingEnabled) {
        return false;
      }

      if (filteredColumnMap[column.id] !== undefined) {
        return filteredColumnMap[column.id];
      }

      return true;
    },
    [isSmartRoutingEnabled, shouldUseAggregateInventory, useAutoSplitEnabled, isCommittedInventoryCountsEnabled],
  );

  const userColumnConfig = tempUserColumnConfig.filter(applyColumnConfigurationFilters);
  const defaultColumnConfigurations = tempDefaultColumnConfigurations.filter(applyColumnConfigurationFilters);

  const columnConfiguration = userColumnConfig.length
    ? userColumnConfig.map((userConf) => {
        const col = defaultColumnConfigurations.find((colConf) => colConf.id === userConf.id);
        return {
          ...col,
          ...userConf,
        };
      })
    : defaultColumnConfigurations;

  return (
    <EditColumnsModal
      baseColumns={defaultColumnConfigurations}
      columnConfiguration={columnConfiguration}
      defaultColumnConfigurations={defaultColumnConfigurations}
      onClose={onClose}
      saveColumnConfiguration={saveColumnConfiguration}
    />
  );
};

EditProductsColumnsModalContainer.propTypes = {
  onClose: PropTypes.func.isRequired,
};
